<template>
  <AppLayout 
    page-title="Store" 
    page-subtitle="Browse courses, tools, and equipment for oilfield professionals"
    :show-cart-button="true"
    :cart-item-count="cartItemCount"
    @cart-click="showCart = true"
  >
    <!-- Store Content -->
    <div class="space-y-8 pt-8">
      <!-- Store Header -->
      <div class="flex items-center justify-between">
        <div>
          <h2 class="text-2xl font-bold text-white mb-2">OilField Store</h2>
          <p class="text-slate-300">Professional tools, courses, and equipment</p>
        </div>
        <div class="flex items-center space-x-4">
          <div class="relative">
            <input 
              v-model="searchQuery" 
              type="text" 
              placeholder="Search products..." 
              class="w-64 px-4 py-2 bg-slate-800/50 border border-slate-700/50 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/50"
            />
            <svg class="absolute right-3 top-2.5 w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
            </svg>
          </div>
        </div>
      </div>

      <!-- Tab Navigation -->
      <div class="border-b border-slate-700/50">
        <nav class="flex space-x-8">
          <button 
            v-for="tab in tabs" 
            :key="tab.id"
            @click="activeTab = tab.id"
            :class="[
              'py-2 px-1 border-b-2 font-medium text-sm transition-colors',
              activeTab === tab.id 
                ? 'border-emerald-500 text-emerald-400' 
                : 'border-transparent text-slate-400 hover:text-slate-300 hover:border-slate-600'
            ]"
          >
            {{ tab.name }}
          </button>
        </nav>
      </div>

      <!-- Tab Content -->
      <div class="min-h-96">
        <!-- Courses Tab -->
        <div v-if="activeTab === 'courses'" class="space-y-6">
          <p v-if="amazonError && !filteredCourses.length" class="text-red-400 text-center">{{ amazonError }}</p>
          <div v-if="isLoadingAmazon && !filteredCourses.length" class="text-center py-12 text-slate-300">Loading Amazon products…</div>
          <div v-else-if="!filteredCourses.length" class="text-center py-12 text-slate-400">No Amazon training products found.</div>
          <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            <div 
              v-for="course in filteredCourses" 
              :key="course.id"
              class="bg-slate-800/50 border border-slate-700/50 rounded-xl overflow-hidden hover:border-emerald-500/50 transition-all duration-300 group"
            >
              <div class="aspect-video bg-gradient-to-br from-emerald-900/20 to-cyan-900/20 relative overflow-hidden">
                <img 
                  :src="course.image" 
                  :alt="course.title"
                  class="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
                <div class="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                <div class="absolute top-3 right-3">
                  <span class="px-2 py-1 bg-emerald-500/90 text-white text-xs font-medium rounded-full">
                    {{ course.level }}
                  </span>
                </div>
              </div>
              
              <div class="p-4">
                <h3 class="text-lg font-semibold text-white mb-2 group-hover:text-emerald-400 transition-colors">
                  {{ course.title }}
                </h3>
                <p class="text-slate-300 text-sm mb-3 line-clamp-2">{{ course.description }}</p>
                
                <div class="flex items-center justify-between mb-3">
                  <div class="flex items-center space-x-2">
                                         <div class="flex items-center">
                       <svg class="w-4 h-4 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                         <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
                       </svg>
                       <span class="text-sm text-slate-400 ml-1">4.8</span>
                     </div>
                    <span class="text-slate-500">•</span>
                    <span class="text-sm text-slate-400">{{ course.duration }}</span>
                  </div>
                  <span class="text-lg font-bold text-emerald-400">${{ course.price }}</span>
                </div>
                
                <a
                  v-if="course.affiliateUrl"
                  :href="course.affiliateUrl"
                  target="_blank"
                  rel="noopener"
                  class="w-full bg-gradient-to-r from-orange-500 to-yellow-500 text-white py-2 px-4 rounded-lg font-medium hover:from-orange-600 hover:to-yellow-600 transition-all duration-300 flex items-center justify-center space-x-2"
                >
                  <span>View on Amazon</span>
                </a>
                <button 
                  v-else
                  @click="addToCart(course)"
                  class="w-full bg-gradient-to-r from-emerald-500 to-cyan-500 text-white py-2 px-4 rounded-lg font-medium hover:from-emerald-600 hover:to-cyan-600 transition-all duration-300 flex items-center justify-center space-x-2"
                >
                  <span>Add to Cart</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Tools Tab -->
        <div v-if="activeTab === 'tools'" class="space-y-6">
          <p v-if="amazonError && !filteredTools.length" class="text-red-400 text-center">{{ amazonError }}</p>
          <div v-if="isLoadingAmazon && !filteredTools.length" class="text-center py-12 text-slate-300">Loading Amazon products…</div>
          <div v-else-if="!filteredTools.length" class="text-center py-12 text-slate-400">No Amazon tools found.</div>
          <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            <div 
              v-for="tool in filteredTools" 
              :key="tool.id"
              class="bg-slate-800/50 border border-slate-700/50 rounded-xl overflow-hidden hover:border-emerald-500/50 transition-all duration-300 group"
            >
              <div class="aspect-square bg-gradient-to-br from-emerald-900/20 to-cyan-900/20 relative overflow-hidden">
                <img 
                  :src="tool.image" 
                  :alt="tool.name || tool.title"
                  class="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
                <div class="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                <div class="absolute top-3 right-3">
                  <span class="px-2 py-1 bg-blue-500/90 text-white text-xs font-medium rounded-full">
                    {{ tool.category }}
                  </span>
                </div>
              </div>
              
              <div class="p-4">
                <h3 class="text-lg font-semibold text-white mb-2 group-hover:text-emerald-400 transition-colors">
                  {{ tool.name || tool.title }}
                </h3>
                <p class="text-slate-300 text-sm mb-3 line-clamp-2">{{ tool.description }}</p>
                
                <div class="flex items-center justify-between mb-3">
                  <div class="flex items-center space-x-2">
                    <div class="flex items-center">
                      <svg class="w-4 h-4 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
                      </svg>
                      <span class="text-sm text-slate-400 ml-1">{{ tool.rating }}</span>
                    </div>
                                         <span class="text-slate-500">•</span>
                     <span class="text-sm text-slate-400">{{ tool.category }}</span>
                  </div>
                  <span class="text-lg font-bold text-emerald-400">${{ tool.price }}</span>
                </div>
                
                <a
                  v-if="tool.affiliateUrl"
                  :href="tool.affiliateUrl"
                  target="_blank"
                  rel="noopener"
                  class="w-full bg-gradient-to-r from-orange-500 to-yellow-500 text-white py-2 px-4 rounded-lg font-medium hover:from-orange-600 hover:to-yellow-600 transition-all duration-300 flex items-center justify-center"
                >View on Amazon</a>
                <button 
                  v-else
                  @click="addToCart(tool)"
                  class="w-full bg-gradient-to-r from-emerald-500 to-cyan-500 text-white py-2 px-4 rounded-lg font-medium hover:from-emerald-600 hover:to-cyan-600 transition-all duration-300 flex items-center justify-center space-x-2"
                >
                  <span>Add to Cart</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Amazon Tab -->
        <div v-if="activeTab === 'amazon'" class="space-y-6">
          <!-- Search Controls -->
          <div class="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6">
            <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div>
                <label class="block text-sm font-medium text-slate-300 mb-2">Search Term</label>
                <input 
                  v-model="amazonSearchParams.keywords" 
                  type="text" 
                  placeholder="e.g., safety equipment, tools..."
                  class="w-full px-3 py-2 bg-slate-700/50 border border-slate-600/50 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/50"
                />
              </div>
              <div>
                <label class="block text-sm font-medium text-slate-300 mb-2">Category</label>
                <select 
                  v-model="amazonSearchParams.category"
                  class="w-full px-3 py-2 bg-slate-700/50 border border-slate-600/50 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-emerald-500/50"
                >
                  <option value="">All Categories</option>
                  <option v-for="category in amazonCategories" :key="category.value" :value="category.value">
                    {{ category.label }}
                  </option>
                </select>
              </div>
              <div>
                <label class="block text-sm font-medium text-slate-300 mb-2">Sort By</label>
                <select 
                  v-model="amazonSearchParams.sortBy"
                  class="w-full px-3 py-2 bg-slate-700/50 border border-slate-600/50 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-emerald-500/50"
                >
                  <option value="relevance">Relevance</option>
                  <option value="price-low">Price: Low to High</option>
                  <option value="price-high">Price: High to Low</option>
                  <option value="rating">Customer Rating</option>
                </select>
              </div>
            </div>
            <button 
              @click="searchAmazonProducts"
              class="bg-gradient-to-r from-emerald-500 to-cyan-500 text-white py-2 px-6 rounded-lg font-medium hover:from-emerald-600 hover:to-cyan-600 transition-all duration-300 flex items-center space-x-2"
            >
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
              </svg>
              <span>Search Amazon</span>
            </button>
          </div>

          <!-- Amazon Products Grid -->
          <div v-if="amazonProducts.length > 0" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            <div 
              v-for="product in amazonProducts" 
              :key="product.id"
              class="bg-slate-800/50 border border-slate-700/50 rounded-xl overflow-hidden hover:border-emerald-500/50 transition-all duration-300 group"
            >
              <div class="aspect-square bg-gradient-to-br from-emerald-900/20 to-cyan-900/20 relative overflow-hidden">
                <img 
                  :src="product.image" 
                  :alt="product.title"
                  class="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
                <div class="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                <div class="absolute top-3 right-3">
                  <span class="px-2 py-1 bg-orange-500/90 text-white text-xs font-medium rounded-full">
                    Amazon
                  </span>
                </div>
              </div>
              
              <div class="p-4">
                <h3 class="text-lg font-semibold text-white mb-2 group-hover:text-emerald-400 transition-colors line-clamp-2">
                  {{ product.title }}
                </h3>
                <p class="text-slate-300 text-sm mb-3 line-clamp-2">{{ product.description }}</p>
                
                <div class="flex items-center justify-between mb-3">
                  <div class="flex items-center space-x-2">
                    <div class="flex items-center">
                      <svg class="w-4 h-4 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
                      </svg>
                      <span class="text-sm text-slate-400 ml-1">{{ product.rating }}</span>
                    </div>
                    <span class="text-slate-500">•</span>
                    <span class="text-sm text-slate-400">{{ product.reviewCount }} reviews</span>
                  </div>
                  <span class="text-lg font-bold text-emerald-400">${{ product.price }}</span>
                </div>
                
                <div class="flex space-x-2">
                  <a 
                    :href="product.affiliateUrl" 
                    target="_blank"
                    class="flex-1 bg-gradient-to-r from-orange-500 to-yellow-500 text-white py-2 px-4 rounded-lg font-medium hover:from-orange-600 hover:to-yellow-600 transition-all duration-300 flex items-center justify-center space-x-2"
                  >
                    <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                    </svg>
                    <span>View on Amazon</span>
                  </a>
                  <button 
                    @click="addToCart(product)"
                    class="px-3 py-2 bg-slate-700/50 text-slate-300 rounded-lg hover:bg-slate-600/50 transition-colors"
                  >
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"/>
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Loading State -->
          <div v-else-if="isLoadingAmazon" class="text-center py-12">
            <div class="inline-flex items-center space-x-2">
              <div class="animate-spin rounded-full h-6 w-6 border-b-2 border-emerald-500"></div>
              <span class="text-slate-300">Searching Amazon products...</span>
            </div>
          </div>

          <div v-else class="text-center py-12">
            <h3 class="text-lg font-medium text-slate-300 mb-2">{{ amazonError || 'No products found' }}</h3>
            <p class="text-slate-400">{{ amazonError ? 'Check Amazon API settings in the admin panel.' : 'Try adjusting your search criteria' }}</p>
          </div>
        </div>

        <!-- Order History Tab -->
        <div v-if="activeTab === 'orders'" class="space-y-6">
          <div class="bg-slate-800/50 border border-slate-700/50 rounded-xl overflow-hidden">
            <div class="px-6 py-4 border-b border-slate-700/50">
              <h3 class="text-lg font-semibold text-white">Order History</h3>
            </div>
            
            <div class="divide-y divide-slate-700/50">
              <div 
                v-for="order in orderHistory" 
                :key="order.id"
                class="p-6 hover:bg-slate-700/30 transition-colors"
              >
                <div class="flex items-center justify-between mb-4">
                  <div>
                    <h4 class="text-white font-medium">Order #{{ order.id }}</h4>
                                         <p class="text-slate-400 text-sm">{{ formatDate(order.orderDate) }}</p>
                  </div>
                  <div class="text-right">
                    <span class="text-lg font-bold text-emerald-400">${{ order.total }}</span>
                    <div class="flex items-center mt-1">
                      <span :class="getOrderStatusClass(order.status)" class="px-2 py-1 rounded-full text-xs font-medium">
                        {{ order.status }}
                      </span>
                    </div>
                  </div>
                </div>
                
                <div class="space-y-2">
                  <div 
                    v-for="item in order.items" 
                    :key="item.id"
                    class="flex items-center justify-between py-2"
                  >
                    <div class="flex items-center space-x-3">
                      <img :src="item.image" :alt="item.name" class="w-10 h-10 rounded-lg object-cover" />
                      <div>
                        <p class="text-white font-medium">{{ item.name }}</p>
                        <p class="text-slate-400 text-sm">Qty: {{ item.quantity }}</p>
                      </div>
                    </div>
                    <span class="text-slate-300">${{ item.price }}</span>
                  </div>
                </div>
                
                <div class="mt-4 pt-4 border-t border-slate-700/50">
                  <div class="flex items-center justify-between text-sm">
                    <span class="text-slate-400">Payment Method:</span>
                    <span class="text-white">{{ formatPaymentMethod(order.paymentMethod) }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Cart Sidebar -->
    <CartSidebar 
      :is-open="showCart" 
      @close="showCart = false"
      @order-complete="handleCheckout"
    />
  </AppLayout>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import AppLayout from '../components/layout/AppLayout.vue'
import CartSidebar from '../components/store/CartSidebar.vue'
import { amazonService, type AmazonProduct, type AmazonSearchParams } from '../services/amazonService'
import { storeService, type Course, type Product } from '../services/storeService'
import { useUserStore } from '../stores/userStore'

const router = useRouter()
const userStore = useUserStore()

// Reactive data
const activeTab = ref('courses')
const searchQuery = ref('')
const showCart = ref(false)
const isLoadingAmazon = ref(false)

// Amazon search parameters
const amazonSearchParams = ref({
  keywords: '',
  category: '',
  sortBy: 'relevance'
})

const amazonCategories = ref([
  { value: 'tools', label: 'Tools & Equipment' },
  { value: 'safety', label: 'Safety Equipment' },
  { value: 'clothing', label: 'Work Clothing' },
  { value: 'electronics', label: 'Electronics' },
  { value: 'books', label: 'Books & Media' }
])

const amazonProducts = ref<AmazonProduct[]>([])
const amazonCourses = ref<AmazonProduct[]>([])
const amazonTools = ref<AmazonProduct[]>([])
const amazonError = ref('')

// Tab configuration
const tabs = [
  { id: 'courses', name: 'Training Courses' },
  { id: 'tools', name: 'Tools & Equipment' },
  { id: 'amazon', name: 'Get It On Amazon' },
  { id: 'orders', name: 'Order History' }
]

// Computed properties
const filteredCourses = computed(() => {
  const q = searchQuery.value.toLowerCase()
  if (!q) return amazonCourses.value
  return amazonCourses.value.filter((course) =>
    `${course.title} ${course.description}`.toLowerCase().includes(q)
  )
})

const filteredTools = computed(() => {
  const q = searchQuery.value.toLowerCase()
  if (!q) return amazonTools.value
  return amazonTools.value.filter((tool) =>
    `${tool.name || tool.title} ${tool.description}`.toLowerCase().includes(q)
  )
})

const cartItemCount = computed(() => storeService.getCartItemCount().value)

const orderHistory = computed(() => storeService.getOrders().value)

// Methods
const addToCart = (item: Product | Course | AmazonProduct) => {
  if ('name' in item) {
    storeService.addToCart(item, 'product')
  } else if ('title' in item && 'instructor' in item) {
    storeService.addToCart(item, 'course')
  } else if ('title' in item && 'affiliateUrl' in item) {
    // Handle Amazon products - convert to Product format
    const productItem: Product = {
      id: parseInt(item.id),
      name: item.title,
      description: item.description,
      price: item.price,
      rating: item.rating,
      image: item.image,
      category: item.category,
      stock: 1,
      sku: item.id
    }
    storeService.addToCart(productItem, 'product')
  }
  // Show success notification
  console.log('Added to cart:', 'name' in item ? item.name : item.title)
}

const searchAmazonProducts = async () => {
  isLoadingAmazon.value = true
  amazonError.value = ''
  try {
    const searchParams: AmazonSearchParams = {
      keywords: amazonSearchParams.value.keywords,
      category: amazonSearchParams.value.category || undefined,
      sortBy: amazonSearchParams.value.sortBy as AmazonSearchParams['sortBy']
    }
    const result = await amazonService.searchCatalog(searchParams)
    amazonProducts.value = result.products
    amazonError.value = result.error
  } catch (error) {
    console.error('Error searching Amazon products:', error)
    amazonProducts.value = []
    amazonError.value = 'Amazon catalog is unavailable.'
  } finally {
    isLoadingAmazon.value = false
  }
}

const loadStoreCatalog = async () => {
  isLoadingAmazon.value = true
  amazonError.value = ''
  try {
    const [courses, tools, featured] = await Promise.all([
      amazonService.searchCatalog({ keywords: '', category: 'training' }),
      amazonService.searchCatalog({ keywords: '', category: 'tools' }),
      amazonService.searchCatalog({ keywords: amazonSearchParams.value.keywords, category: amazonSearchParams.value.category || undefined })
    ])
    amazonCourses.value = courses.products
    amazonTools.value = tools.products
    amazonProducts.value = featured.products
    amazonError.value = courses.error || tools.error || featured.error
  } finally {
    isLoadingAmazon.value = false
  }
}

const handleCheckout = () => {
  showCart.value = false
  // Navigate to checkout or show checkout modal
  console.log('Proceeding to checkout...')
}

const formatDate = (date: string) => {
  return new Date(date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  })
}

const getOrderStatusClass = (status: string) => {
  switch (status.toLowerCase()) {
    case 'completed':
      return 'bg-green-500/20 text-green-400'
    case 'processing':
      return 'bg-yellow-500/20 text-yellow-400'
    case 'shipped':
      return 'bg-blue-500/20 text-blue-400'
    case 'cancelled':
      return 'bg-red-500/20 text-red-400'
    default:
      return 'bg-slate-500/20 text-slate-400'
  }
}

const formatPaymentMethod = (method: string) => {
  switch (method.toLowerCase()) {
    case 'credit_card':
      return 'Credit Card'
    case 'paypal':
      return 'PayPal'
    case 'bank_transfer':
      return 'Bank Transfer'
    default:
      return method
  }
}

// Lifecycle
onMounted(() => {
  loadStoreCatalog()
})
</script>

<style scoped>
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
